import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { Reference } from '../../hooks/useFindReferences';

type ReferencesModalProps = {
  isOpen: boolean;
  symbolName: string;
  references: Reference[];
  selectedIndex: number;
  onSelect: (ref: Reference) => void;
  onClose: () => void;
  onNavigate: (direction: 'up' | 'down') => void;
};

export const ReferencesModal = React.memo(function ReferencesModal({
  isOpen,
  symbolName,
  references,
  selectedIndex,
  onSelect,
  onClose,
  onNavigate,
}: ReferencesModalProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = React.useState('');

  // Filter references by path
  const filteredRefs = useMemo(() => {
    if (!filter) return references;
    const lowerFilter = filter.toLowerCase();
    return references.filter(ref =>
      ref.path.toLowerCase().includes(lowerFilter) ||
      ref.preview.toLowerCase().includes(lowerFilter)
    );
  }, [references, filter]);

  // Group references by file
  const groupedRefs = useMemo(() => {
    const groups: Record<string, Reference[]> = {};
    for (const ref of filteredRefs) {
      if (!groups[ref.path]) {
        groups[ref.path] = [];
      }
      groups[ref.path].push(ref);
    }
    return groups;
  }, [filteredRefs]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onNavigate('up');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onNavigate('down');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredRefs[selectedIndex]) {
        onSelect(filteredRefs[selectedIndex]);
      }
    }
  }, [onClose, onNavigate, onSelect, filteredRefs, selectedIndex]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setFilter('');
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector('.ref-item.selected');
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  let flatIndex = 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="references-modal" onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="references-header">
          <span className="references-title">
            References to <strong>{symbolName}</strong>
          </span>
          <span className="references-count">{filteredRefs.length} references</span>
        </div>
        <input
          ref={inputRef}
          type="text"
          className="references-filter"
          placeholder="Filter by file or content..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="references-list" ref={listRef}>
          {Object.entries(groupedRefs).map(([path, refs]) => (
            <div key={path} className="ref-group">
              <div className="ref-group-header">
                <span className="ref-file-icon">📄</span>
                <span className="ref-file-path">{path}</span>
                <span className="ref-file-count">{refs.length}</span>
              </div>
              {refs.map((ref) => {
                const currentIndex = flatIndex++;
                const isSelected = currentIndex === selectedIndex;
                return (
                  <button
                    key={`${ref.line}-${ref.column}`}
                    className={`ref-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => onSelect(ref)}
                  >
                    <span className="ref-line-number">{ref.line}</span>
                    <span className="ref-preview">
                      {ref.preview}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
          {filteredRefs.length === 0 && (
            <div className="references-empty">
              {references.length === 0 ? 'No references found' : 'No matching references'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
